import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setFilterQuery, setIsSavedFilterAppliedFilters } from '../../../store/filterSlice';
import Loader from '../../../components/Common/Loader';

const FilterContext = createContext();

export const useFilters = () => useContext(FilterContext);

export const FilterProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [reloadFilter, setReloadFilter] = useState(true);
  const [savedFilterId, setSavedFilterId] = useState(null);
  const [tempFilters, setTempFilters] = useState({});
  const [filters, setFilters] = useState({});
  const init = async () => {
    const tempFilter = (await JSON.parse(localStorage.getItem('tempFilter'))) || {};
    if (tempFilter) {
      dispatch(setFilterQuery(tempFilter));
      dispatch(setIsSavedFilterAppliedFilters());
      setFilters(tempFilter);
      setIsInitialized(true);
    }
  };
  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const temp = { ...filters };
    if (temp && typeof temp === 'object') {
      delete temp['searchTerm'];
      delete temp['limit'];
      delete temp['page'];
    }
    localStorage.setItem('tempFilter', JSON.stringify(temp));
  }, [filters]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      if ('page' in prev && key != 'page' && (prev?.page?.[0] > 1 || prev?.page?.[0] != '1')) {
        return { ...prev, [key]: [value], page: ['1'] };
      } else {
        return { ...prev, [key]: [value] };
      }
    });
  }, []);

  const updateTempFilter = useCallback((list) => {
    setTempFilters((prev) => ({ ...prev, ...list }));
  }, []);

  const applyButtonFilter = useCallback(
    (key, value) => {
      setFilters((prev) => {
        if ('page' in prev && (prev?.page?.[0] > 1 || prev?.page?.[0] != '1')) {
          return { ...prev, ...tempFilters, page: ['1'] };
        } else {
          return { ...prev, ...tempFilters };
        }
      });
    },
    [tempFilters, filters]
  );

  const handleSetSavedFilterId = (id) => {
    setSavedFilterId(id);
  };
  const updateFilterByList = useCallback((list) => {
    setFilters((prev) => ({ ...prev, ...list }));
  }, []);

  const addFilterList = useCallback((list, id = null) => {
    if (id) {
      setSavedFilterId(id);
    }
    if (Object.keys(list).length <= 0) {
      setTempFilters(list)
    }
    setFilters((prev) => list);
  }, []);

  const updateFilterByListTags = useCallback((list) => {
    setFilters((prev) => {
      if (prev?.excludeTags?.length > 0 || prev?.includeTags?.length > 0) {
        dispatch(setFilterQuery({ ...prev, ...list }));
        return { ...prev, ...list };
      }
      dispatch(setFilterQuery(prev));
      return prev;
    });
  }, []);

  // const addFilter = useCallback((key, value) => {
  //   setFilters((prev) => {
  //     if ('page' in prev && (prev?.page?.[0] > 1 || prev?.page?.[0] != '1')) {
  //       const newData = {
  //         ...prev,
  //         page: ['1'],
  //         [key]: prev[key]?.includes(value) ? prev[key].filter((f) => f !== value) : [...(prev[key] || []), value]
  //       };
  //       dispatch(setFilterQuery(newData));
  //       return newData;
  //     } else {
  //       const newData = {
  //         ...prev,
  //         [key]: prev[key]?.includes(value) ? prev[key].filter((f) => f !== value) : [...(prev[key] || []), value]
  //       };
  //       dispatch(setFilterQuery(newData));
  //       return newData;
  //     }
  //   });
  // }, []);
  const addFilter = useCallback((key, value) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [key]: prev[key]?.includes(value) ? prev[key].filter(f => f !== value) : [...(prev[key] || []), value],
        page: ['1']  // Assuming you always want to reset to page 1 on filter change
      };
      // Update local storage asynchronousl
      localStorage.setItem('tempFilter', JSON.stringify(updated));
      dispatch(setFilterQuery(updated));
      return updated;
    });
  }, []);

  const buildQuery = useCallback(() => {
    return Object.entries(filters)
      ?.map(([key, values]) => {
        // Filter out undefined values before encoding
        const filteredValues = values.filter(value => value !== undefined && value !== null);
        return `${encodeURIComponent(key)}=${filteredValues.map(value => encodeURIComponent(value)).join(',')}`;
      })
      .join('&');
  }, [filters]);

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <FilterContext.Provider
      value={{
        filters,
        savedFilterId,
        updateFilter,
        buildQuery,
        updateTempFilter,
        applyButtonFilter,
        tempFilters,
        handleSetSavedFilterId,
        addFilter,
        updateFilterByList,
        updateFilterByListTags,
        addFilterList,
        setReloadFilter,
        reloadFilter
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => {
  const {
    updateFilter,
    buildQuery,
    handleSetSavedFilterId,
    tempFilters,
    savedFilterId,
    updateFilterByListTags,
    filters,
    updateTempFilter,
    applyButtonFilter,
    addFilterList,
    addFilter,
    updateFilterByList,
    setReloadFilter,
    reloadFilter
  } = useFilters();
  return {
    updateFilter,
    buildQuery,
    filters,
    savedFilterId,
    handleSetSavedFilterId,
    addFilter,
    addFilterList,
    updateFilterByListTags,
    updateFilterByList,
    tempFilters,
    updateTempFilter,
    applyButtonFilter,
    setReloadFilter,
    reloadFilter
  };
};
